import React, { useState, useEffect, type CSSProperties } from 'react';
import type { AuthorJointArticle } from '../types';
import { authorApi } from '@/features/workspace/api';

interface Props {
  onClose: () => void;
}

const STAGE_META: Record<string, { label: string; color: string }> = {
  PREPARATION: { label: 'Подготовка текста', color: '#8faebb' },
  COAUTHOR_SEARCH: { label: 'Набор соавторов', color: '#e2ad3a' },
  SUBMITTED: { label: 'Подана в журнал', color: '#41d2ff' },
  UNDER_REVIEW: { label: 'На рецензии', color: '#9e80ff' },
  ACCEPTED: { label: 'Принята к публикации', color: '#49ddb7' },
  PUBLISHED: { label: 'Опубликована (DOI)', color: '#49ddb7' },
};

const PAYMENT_META: Record<string, { label: string; color: string }> = {
  NOT_PAID: { label: 'Не оплачено', color: '#ff7b65' },
  PARTIALLY_PAID: { label: 'Частично оплачено', color: '#e2ad3a' },
  FULLY_PAID: { label: 'Оплачено 100%', color: '#49ddb7' },
};

export const JointArticlesOverlay: React.FC<Props> = ({ onClose }) => {
  const [articles, setArticles] = useState<AuthorJointArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const data = await authorApi.getJointArticles();
        setArticles(data);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Соавторские проекты</small>
              <h2>Совместные статьи и коллективные заказы</h2>
            </div>
          </div>
          <div className="cab-top-actions">
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              ✕
            </button>
          </div>
        </div>

        <div className="cab-body">
          {loading ? (
            <div className="cabinet-note">Загрузка совместных статей…</div>
          ) : articles.length === 0 ? (
            <div className="cabinet-note">Совместных статей пока нет</div>
          ) : (
            <div className="cabinet-stack">
              {articles.map((art) => {
                const stage = STAGE_META[art.workflow_stage] || {
                  label: art.workflow_stage,
                  color: '#8faebb',
                };
                const payment = PAYMENT_META[art.payment_status] || {
                  label: art.payment_status,
                  color: '#8faebb',
                };
                return (
                  <article
                    key={art.id}
                    className="cabinet-card"
                    style={{ ['--accent' as string]: '#ff7b65' }}
                  >
                    <div className="cabinet-actions" style={{ marginBottom: 8, alignItems: 'center' }}>
                      <span className="cabinet-badge" style={{ '--badge': '#ff7b65' } as CSSProperties}>
                        {art.article_number}
                      </span>
                      <span className="cabinet-badge" style={{ '--badge': stage.color } as CSSProperties}>
                        {stage.label}
                      </span>
                      <span className="cabinet-badge" style={{ '--badge': '#8faebb' } as CSSProperties}>
                        {art.target_database}
                        {art.quartile ? ` (${art.quartile})` : ''}
                      </span>
                      <span className="cabinet-spacer" />
                      <span className="cabinet-badge" style={{ '--badge': payment.color } as CSSProperties}>
                        {payment.label}
                      </span>
                    </div>
                    <strong>{art.title}</strong>
                    <p style={{ margin: '8px 0 0', fontSize: 12, color: '#8faebb' }}>
                      Журнал: {art.target_journal} · Соавторов: {art.coauthors_count} · Куратор:{' '}
                      {art.current_responsible}
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: 12, fontWeight: 700 }}>
                      {art.paid_amount_kzt.toLocaleString()} / {art.total_amount_kzt.toLocaleString()} ₸
                    </p>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
